import { action, IObservableArray, makeObservable, observable } from "mobx";

import { fetchNotificationsForUser } from "@framework/notifications/graphql/fetch-notifications-for-user";
import {
  AnyNotification,
  UserStore as UserStoreType,
  NotificationsStore as NotificationsStoreType,
  NotificationsListStore as NotificationsListStoreType,
  NotificationsFilter,
} from "@voxmedia/duet-context/context/notifications-context";

class NotificationsListStore implements NotificationsListStoreType {
  public notifications: IObservableArray<AnyNotification>;
  public hasNext: boolean;
  public cursor: string | null;
  public loading: boolean;
  public userStore: UserStoreType;
  public notificationsStore: NotificationsStoreType;
  public constructor(
    userStore: UserStoreType,
    notificationsStore: NotificationsStoreType,
  ) {
    this.notifications = observable.array([]);
    this.hasNext = false;
    this.cursor = null;
    this.userStore = userStore;
    this.notificationsStore = notificationsStore;
    this.loading = false;
    makeObservable(this, {
      notifications: observable,
      hasNext: observable,
      cursor: observable,
      loadNotificationsForUser: action,
      loading: observable,
      setLoading: action,
      setNotifications: action,
      setHasNext: action,
      setCursor: action,
      setItems: action,
    });
  }

  public setLoading(loading: boolean) {
    this.loading = loading;
  }

  public setNotifications(notifications: AnyNotification[]) {
    this.notifications = observable.array(notifications);
  }

  public setHasNext(hasNext: boolean) {
    this.hasNext = hasNext;
  }

  public setCursor(cursor: string | null) {
    this.cursor = cursor;
  }

  public setItems(items: AnyNotification[]) {
    const updatedItems = [];
    for (const item of items) {
      const added = this.notificationsStore.insert(item);
      if (!added) {
        continue;
      }
      updatedItems.push(added);
    }
    const newIds = new Set(updatedItems.map((n) => n.id));
    this.notifications.replace([
      ...this.notifications.filter((n) => !newIds.has(n.id)),
      ...updatedItems,
    ]);
  }

  async loadNotificationsForUser(
    params: {
      cursor?: string;
      filter?: NotificationsFilter;
      count?: number;
    } = {},
  ) {
    if (!this.userStore.viewer?.token) {
      this.setLoading(false);
      this.notifications = observable.array([]);
      this.setHasNext(false);
      this.setCursor(null);
      return;
    }

    this.setLoading(true);

    const notificationsResult = await fetchNotificationsForUser({
      token: this.userStore.viewer.token,
      cursor: params.cursor,
      filter: params.filter,
      count: params.count,
    });

    this.setLoading(false);

    this.setItems([...this.notifications, ...notificationsResult.items]);

    this.setHasNext(notificationsResult.hasNext);
    this.setCursor(notificationsResult.nextCursor ?? null);
  }

  public async refreshNotifications(params?: { filter?: NotificationsFilter }) {
    if (!this.userStore.viewer?.token) {
      return;
    }

    this.setNotifications([]);
    this.setHasNext(false);
    this.setCursor(null);
    await this.loadNotificationsForUser(params);
  }
}

export default NotificationsListStore;
